import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { adminAuth, sendDigestToMe } from '../lib/adminApi';
import { ALLOW_DUPLICATE_USER_EMAILS } from '../lib/testFlags';
import { useNavigate } from 'react-router-dom';
import { sendEmail, welcomeEmail, internalWelcomeEmail, passwordEmail, internalPasswordEmail } from '../lib/email';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import EmailTemplatesPanel from '../components/EmailTemplatesPanel';
import Sidebar from '../components/Sidebar';
import FilterDropdown from '../components/FilterDropdown';
import SearchInput from '../components/SearchInput';
import SupplierCombobox from '../components/SupplierCombobox';


export default function Admin() {
  const navigate = useNavigate();
  const { user: currentUser, loading: currentUserLoading } = useCurrentInternalUser();
  // Gate: only admin / super_user roles may view this page. Other internal users
  // (e.g. finance) get bounced back to the dashboard. Wait for the role lookup
  // to finish before deciding so we don't redirect a legitimate admin mid-load.
  useEffect(() => {
    if (currentUserLoading) return;
    if (!currentUser || !isAdmin(currentUser)) {
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, currentUserLoading, navigate]);
  const [activeTab, setActiveTab] = useState('users');
  const [internalUsers, setInternalUsers] = useState([]);
  const [supplierUsers, setSupplierUsers] = useState([]);
  const [listValues, setListValues] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedList, setSelectedList] = useState('supplier_category');
  const [loading, setLoading] = useState(true);
  const [newValue, setNewValue] = useState('');
  // Tickbox state for the "Show in Risk Flag Configuration" checkbox
  // that sits next to the new-value input when the document_type list
  // is selected. Reset back to false after each successful add so the
  // default for new types stays "no risk requirement".
  const [newValueRiskConfig, setNewValueRiskConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Brief auto-archive system setting (app_settings: enabled + weeks).
  const [archiveEnabled, setArchiveEnabled] = useState(true);
  const [archiveWeeks, setArchiveWeeks] = useState('4');
  const [archiveSaving, setArchiveSaving] = useState(false);
  // Post-event feedback value threshold (app_settings: feedback_value_threshold_gbp).
  // 0 = every award qualifies.
  const [feedbackThreshold, setFeedbackThreshold] = useState('0');
  const [feedbackThresholdSaving, setFeedbackThresholdSaving] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  // "Email me today's round-up" — see the digest now rather than waiting for
  // tomorrow's 8am run. Also the fastest way to find out WHY it isn't arriving:
  // the endpoint reports what it found, not merely whether it worked.
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestResult, setDigestResult] = useState(null);
  const [showAddSupplierUser, setShowAddSupplierUser] = useState(false);
  const [editingSupplierUser, setEditingSupplierUser] = useState(null);
  const [editingInternalUser, setEditingInternalUser] = useState(null);
  const [internalUserMode, setInternalUserMode] = useState('view');
  const [supplierUserMode, setSupplierUserMode] = useState('view');
  const [editingPassword, setEditingPassword] = useState('');
  const [editingPasswordRevealed, setEditingPasswordRevealed] = useState(false);
  // True only when the password in state matches what's stored in Supabase
  // (i.e. it was just generated or just saved). Typing into the field flips it
  // to false so the "Send Password Email" button can't ship an uncommitted value.
  const [editingPasswordCommitted, setEditingPasswordCommitted] = useState(false);
  const [internalSearch, setInternalSearch] = useState('');
  const [internalJobTitleFilter, setInternalJobTitleFilter] = useState([]);
  const [internalDepartmentFilter, setInternalDepartmentFilter] = useState([]);
  const [internalRoleFilter, setInternalRoleFilter] = useState([]);
  const [internalStatusFilter, setInternalStatusFilter] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierIdFilter, setSupplierIdFilter] = useState([]);
  const [supplierStatusFilter, setSupplierStatusFilter] = useState([]);
  const [newInternalUser, setNewInternalUser] = useState({ full_name: '', email: '', role: 'standard_user', job_title: '', department: '', password: '', compliance_alert_emails: false, safety_incident_emails: false, daily_roundup_emails: false, send_emails: true });
  const [newSupplierUser, setNewSupplierUser] = useState({ full_name: '', email: '', supplier_id: '', password: '', send_emails: true });
  // Custom document types added by admins via the List Values tab.

  const EyeIcon = ({ show }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      {show
        ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
        : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></>
      }
    </svg>
  );

  const EnvelopeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );

  const KeyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a4 4 0 110 8 4 4 0 010-8zm-4 4l-7 7v3h3v-2h2v-2h2l1-1" />
    </svg>
  );

  const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-1 12a2 2 0 01-2 2H8a2 2 0 01-2-2L5 7m5 4v6m4-6v6M4 7h16M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
    </svg>
  );

  const RefreshIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M4 9a8 8 0 0114-4M20 15a8 8 0 01-14 4" />
    </svg>
  );

  const CopyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 4h8a2 2 0 012 2v12a2 2 0 01-2 2H8a2 2 0 01-2-2V6a2 2 0 012-2zm-2 14H4a2 2 0 01-2-2V4a2 2 0 012-2h8a2 2 0 012 2v2" />
    </svg>
  );

  // Tab icons. Lucide-style hand-rolled SVGs — kept inline to match the
  // rest of the file's icon convention (the project doesn't bundle a
  // lucide-react dep). All 14px square, currentColor stroke so they
  // inherit the tab's active/inactive colour state.
  const TabIconUsers = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
  const TabIconBriefcase = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
  const TabIconList = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
  const TabIconSettings = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );

  // Reset the inline-password field whenever an edit panel closes or opens for a different user.
  useEffect(() => {
    setEditingPassword('');
    setEditingPasswordRevealed(false);
    setEditingPasswordCommitted(false);
  }, [editingInternalUser?.id, editingSupplierUser?.id]);

  const listNames = [
    { key: 'supplier_category', label: 'Supplier Categories' },
    { key: 'region', label: 'Regions' },
    { key: 'venue_type', label: 'Venue Types' },
    { key: 'company_size', label: 'Company Sizes' },
    { key: 'max_project_size', label: 'Max Project Sizes' },
    { key: 'preferred_supplier_status', label: 'Preferred Supplier Status' },
    { key: 'do_not_use_status', label: 'Do Not Use Status' },
    { key: 'compliance_status', label: 'Compliance Status' },
    { key: 'inc_ex_vat', label: 'Inc / Ex VAT' },
    { key: 'document_type', label: 'Document Types' },
    { key: 'compliance_reject_reason', label: 'Compliance — Reject Reasons' },
    { key: 'compliance_request_info_reason', label: 'Compliance — Request-Info Reasons' },
  ];

  // Welcome / password senders. sendEmail throws on failure; these
  // helpers are called both awaited (the resend handlers) AND fire-
  // and-forget (the post-create flows). Catching here keeps both
  // call paths safe — an unhandled-rejection in a fire-and-forget
  // would otherwise surface as a red error in the console without
  // any in-app indicator. The resend handlers below set their own
  // success toast separately.
  const sendInternalWelcomeEmail = async (name, email) => {
    const { subject, html } = internalWelcomeEmail({ fullName: name, email, appUrl: window.location.origin });
    try { await sendEmail({ to: email, subject, html }); } catch (err) {
      console.warn('internalWelcomeEmail failed', { to: email, error: err?.message });
    }
  };

  const sendSupplierWelcomeEmail = async (name, email) => {
    const { subject, html, text } = welcomeEmail({ fullName: name, email, appUrl: window.location.origin });
    try { await sendEmail({ to: email, subject, html, text }); } catch (err) {
      console.warn('welcomeEmail failed', { to: email, error: err?.message });
    }
  };

  const sendInternalPasswordEmail = async (name, email, password) => {
    const { subject, html } = internalPasswordEmail({ fullName: name, email, password, appUrl: window.location.origin });
    try { await sendEmail({ to: email, subject, html }); } catch (err) {
      console.warn('internalPasswordEmail failed', { to: email, error: err?.message });
    }
  };

  const sendSupplierPasswordEmail = async (name, email, password) => {
    const { subject, html, text } = passwordEmail({ fullName: name, email, password, appUrl: window.location.origin });
    try { await sendEmail({ to: email, subject, html, text }); } catch (err) {
      console.warn('passwordEmail failed', { to: email, error: err?.message });
    }
  };

  const resendInternalWelcomeEmail = async (user) => {
    if (!window.confirm(`Send a welcome email to ${user.full_name} (${user.email})?`)) return;
    setSaving(true);
    setError(null);
    await sendInternalWelcomeEmail(user.full_name, user.email);
    setSuccess(`Welcome email sent to ${user.email}.`);
    setTimeout(() => setSuccess(null), 3000);
    setSaving(false);
  };

  const resendSupplierWelcomeEmail = async (user) => {
    if (!window.confirm(`Send a welcome email to ${user.full_name} (${user.email})?`)) return;
    setSaving(true);
    setError(null);
    await sendSupplierWelcomeEmail(user.full_name, user.email);
    setSuccess(`Welcome email sent to ${user.email}.`);
    setTimeout(() => setSuccess(null), 3000);
    setSaving(false);
  };

  const getListValues = useCallback(async () => {
    const { data } = await supabase.from('list_values').select('*').eq('list_name', selectedList).order('display_order');
    setListValues(data || []);
  }, [selectedList]);

  const refreshUsers = async () => {
    const { data: iu } = await supabase.from('internal_users').select('*').order('full_name');
    const { data: su } = await supabase.from('supplier_users').select('*, suppliers(supplier_name)').order('full_name');
    setInternalUsers(iu || []);
    setSupplierUsers(su || []);
  };
  // Pulls admin-managed document_type list_values. Kept in its own
  // state (not just sourced from listValues) so the upload dropdown
  // has the custom types available even when the admin isn't sitting
  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      const { data: iu } = await supabase.from('internal_users').select('*').order('full_name');
      const { data: su } = await supabase.from('supplier_users').select('*, suppliers(supplier_name)').order('full_name');
      const { data: sups } = await supabase.from('suppliers').select('id, supplier_name').order('supplier_name');
      setInternalUsers(iu || []);
      setSupplierUsers(su || []);
      setSuppliers(sups || []);
      setLoading(false);
    };
    getData();
  }, []);

  useEffect(() => {
    getListValues();
  }, [getListValues]);

  // Load the brief auto-archive system setting.
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('app_settings').select('key, value')
        .in('key', ['auto_archive_briefs_enabled', 'auto_archive_briefs_weeks', 'feedback_value_threshold_gbp']);
      for (const row of data || []) {
        if (row.key === 'auto_archive_briefs_enabled') setArchiveEnabled(row.value !== 'false');
        if (row.key === 'auto_archive_briefs_weeks') setArchiveWeeks(String(row.value || '4'));
        if (row.key === 'feedback_value_threshold_gbp') setFeedbackThreshold(String(row.value || '0'));
      }
    })();
  }, []);

  // Build today's round-up and email it to the signed-in admin.
  //
  // The message reports WHAT HAPPENED, not just whether the request succeeded.
  // "Sent" and "sent, but a real run would have stayed quiet because there was
  // nothing to report" are different answers, and so is "the server couldn't
  // read its data" — that last one is what silently produced an empty digest
  // and no email for weeks.
  const sendDigestNow = async () => {
    if (sendingDigest) return;
    setSendingDigest(true);
    setDigestResult(null);
    const r = await sendDigestToMe();
    setSendingDigest(false);

    if (!r.ok) {
      const detail = r.broken?.length
        ? ` Failing queries: ${r.broken.map(b => b.query).join(', ')}.`
        : (r.detail ? ` ${r.detail}` : '');
      setDigestResult({ tone: 'red', msg: `${r.error || 'Could not send the round-up.'}${detail}` });
      return;
    }
    if (r.sent > 0) {
      setDigestResult({
        tone: 'green',
        msg: r.wouldSendOnSchedule
          ? `Sent to ${r.sentTo}. ${r.slipping} slipping, ${r.adminTasks} admin ${r.adminTasks === 1 ? 'task' : 'tasks'}. ${r.optedIn} ${r.optedIn === 1 ? 'person is' : 'people are'} opted in to the scheduled run.`
          : `Sent to ${r.sentTo} — but there is genuinely nothing to report today, so the 8am run would have stayed quiet. That is by design: a digest that arrives every morning saying "all clear" stops being read.`,
      });
      return;
    }
    // Sent nothing, and said why.
    setDigestResult({ tone: 'red', msg: r.skipped ? `Nothing sent — ${r.skipped}.` : 'Nothing sent, and the server gave no reason.' });
  };

  const saveFeedbackThreshold = async (nextValue) => {
    setFeedbackThresholdSaving(true);
    setError(null);
    const amount = Math.max(0, Math.round(Number(nextValue) || 0));
    const stamp = { updated_at: new Date().toISOString(), updated_by: currentUser?.id || null };
    const { error: e } = await supabase.from('app_settings')
      .upsert({ key: 'feedback_value_threshold_gbp', value: String(amount), ...stamp }, { onConflict: 'key' });
    setFeedbackThresholdSaving(false);
    if (e) { setError(e.message); return; }
    setFeedbackThreshold(String(amount));
    setSuccess('Feedback threshold saved');
    setTimeout(() => setSuccess(null), 3000);
  };

  const saveArchiveSettings = async (nextEnabled, nextWeeks) => {
    setArchiveSaving(true);
    setError(null);
    const weeks = Math.max(1, Math.min(52, parseInt(nextWeeks, 10) || 4));
    const stamp = { updated_at: new Date().toISOString(), updated_by: currentUser?.id || null };
    const { error: e1 } = await supabase.from('app_settings')
      .upsert({ key: 'auto_archive_briefs_enabled', value: nextEnabled ? 'true' : 'false', ...stamp }, { onConflict: 'key' });
    const { error: e2 } = await supabase.from('app_settings')
      .upsert({ key: 'auto_archive_briefs_weeks', value: String(weeks), ...stamp }, { onConflict: 'key' });
    setArchiveSaving(false);
    if (e1 || e2) { setError((e1 || e2).message); return; }
    setArchiveEnabled(nextEnabled);
    setArchiveWeeks(String(weeks));
    setSuccess('Auto-archive setting saved');
    setTimeout(() => setSuccess(null), 3000);
  };

const createInternalUser = async () => {
    if (!newInternalUser.full_name || !newInternalUser.email || !newInternalUser.password) {
      setError('Please fill in all fields'); return;
    }
    setSaving(true);
    setError(null);

    // Check email doesn't already exist in either table
    // TESTING: gated by ALLOW_DUPLICATE_USER_EMAILS (src/lib/testFlags.js) — roll back for prod.
    if (!ALLOW_DUPLICATE_USER_EMAILS) {
      const { data: existingInternal } = await supabase.from('internal_users').select('id').eq('email', newInternalUser.email).single();
      const { data: existingSupplier } = await supabase.from('supplier_users').select('id').eq('email', newInternalUser.email).single();
      if (existingInternal || existingSupplier) {
        setError('A user with this email address already exists in the system'); setSaving(false); return;
      }
    }

    let authUserId;
    let relinked = false;
    const created = await adminAuth.createUser({
      email: newInternalUser.email,
      password: newInternalUser.password,
    });
    if (!created.ok) {
      if (created.code !== 'email_exists') { setError(created.error || 'Could not create the account'); setSaving(false); return; }
      // Orphaned auth account — pre-check confirmed no internal_users / supplier_users row
      // for this email, so re-link to the existing auth user.
      const existing = await findAuthUserByEmail(newInternalUser.email);
      if (!existing) {
        setError("This email exists in Supabase Auth but I couldn't find the orphaned account to re-link. Clean it up manually in Supabase → Authentication → Users, then try again.");
        setSaving(false); return;
      }
      if (!window.confirm(`An auth account with email ${newInternalUser.email} already exists but isn't linked to any Supply Pilot user. Re-link this account to ${newInternalUser.full_name}? Their password will be reset to the temporary password you've entered.`)) {
        setSaving(false); return;
      }
      const updRes = await adminAuth.updateUser(existing.id, { password: newInternalUser.password });
      if (!updRes.ok) { setError(`Found existing auth account but couldn't reset its password: ${updRes.error}`); setSaving(false); return; }
      authUserId = existing.id;
      relinked = true;
    } else {
      authUserId = created.id;
    }
    const { error: dbError } = await supabase.from('internal_users').insert([{
      id: authUserId,
      full_name: newInternalUser.full_name,
      email: newInternalUser.email,
      role: newInternalUser.role,
      job_title: newInternalUser.job_title || null,
      department: newInternalUser.department || null,
      compliance_alert_emails: Boolean(newInternalUser.compliance_alert_emails),
      safety_incident_emails: Boolean(newInternalUser.safety_incident_emails),
      daily_roundup_emails: Boolean(newInternalUser.daily_roundup_emails),
    }]);
    if (dbError) { setError(dbError.message); setSaving(false); return; }
    setSuccess(relinked
      ? `Re-linked existing auth account for ${newInternalUser.full_name}.`
      : `User ${newInternalUser.full_name} created successfully!`);
    setTimeout(() => setSuccess(null), 4000);
    // Welcome + password emails are opt-out via the "Send welcome
    // email & password" checkbox on the form. Default is to send so
    // the existing flow is preserved; admins set up a user silently
    // by unticking before clicking Create.
    if (newInternalUser.send_emails) {
      sendInternalWelcomeEmail(newInternalUser.full_name, newInternalUser.email);
      sendInternalPasswordEmail(newInternalUser.full_name, newInternalUser.email, newInternalUser.password);
    }
    setNewInternalUser({ full_name: '', email: '', role: 'standard_user', job_title: '', department: '', password: '', compliance_alert_emails: false, safety_incident_emails: false, daily_roundup_emails: false, send_emails: true });
    setShowAddUser(false);
    await refreshUsers();
    setSaving(false);
  };

const createSupplierUser = async () => {
    if (!newSupplierUser.full_name || !newSupplierUser.email || !newSupplierUser.password || !newSupplierUser.supplier_id) {
      setError('Please fill in all fields'); return;
    }
    setSaving(true);
    setError(null);

    // Check email doesn't already exist in either table
    // TESTING: gated by ALLOW_DUPLICATE_USER_EMAILS (src/lib/testFlags.js) — roll back for prod.
    if (!ALLOW_DUPLICATE_USER_EMAILS) {
      const { data: existingInternal } = await supabase.from('internal_users').select('id').eq('email', newSupplierUser.email).single();
      const { data: existingSupplier } = await supabase.from('supplier_users').select('id').eq('email', newSupplierUser.email).single();
      if (existingInternal || existingSupplier) {
        setError('A user with this email address already exists in the system'); setSaving(false); return;
      }
    }

    let authUserId;
    let relinked = false;
    const created = await adminAuth.createUser({
      email: newSupplierUser.email,
      password: newSupplierUser.password,
    });
    if (!created.ok) {
      if (created.code !== 'email_exists') { setError(created.error || 'Could not create the account'); setSaving(false); return; }
      const existing = await findAuthUserByEmail(newSupplierUser.email);
      if (!existing) {
        setError("This email exists in Supabase Auth but I couldn't find the orphaned account to re-link. Clean it up manually in Supabase → Authentication → Users, then try again.");
        setSaving(false); return;
      }
      if (!window.confirm(`An auth account with email ${newSupplierUser.email} already exists but isn't linked to any Supply Pilot user. Re-link this account to ${newSupplierUser.full_name}? Their password will be reset to the temporary password you've entered.`)) {
        setSaving(false); return;
      }
      const updRes = await adminAuth.updateUser(existing.id, { password: newSupplierUser.password });
      if (!updRes.ok) { setError(`Found existing auth account but couldn't reset its password: ${updRes.error}`); setSaving(false); return; }
      authUserId = existing.id;
      relinked = true;
    } else {
      authUserId = created.id;
    }
    const { error: dbError } = await supabase.from('supplier_users').insert([{
      id: authUserId,
      full_name: newSupplierUser.full_name,
      email: newSupplierUser.email,
      supplier_id: newSupplierUser.supplier_id,
      // Active on creation so the portal user is immediately routable for
      // brief/award comms — don't depend on the DB default or a later step.
      is_active: true,
    }]);
    if (dbError) { setError(dbError.message); setSaving(false); return; }
    setSuccess(relinked
      ? `Re-linked existing auth account for ${newSupplierUser.full_name}.`
      : `Supplier user ${newSupplierUser.full_name} created successfully!`);
    setTimeout(() => setSuccess(null), 4000);
    // Opt-out via "Send welcome email & password" checkbox; default
    // is to send (matches the previous unconditional behaviour).
    if (newSupplierUser.send_emails) {
      sendSupplierWelcomeEmail(newSupplierUser.full_name, newSupplierUser.email);
      sendSupplierPasswordEmail(newSupplierUser.full_name, newSupplierUser.email, newSupplierUser.password);
    }
    setNewSupplierUser({ full_name: '', email: '', supplier_id: '', password: '', send_emails: true });
    setShowAddSupplierUser(false);
    await refreshUsers();
    setSaving(false);
  };

  const updateInternalUser = async () => {
    if (!editingInternalUser.full_name || !editingInternalUser.email) {
      setError('Full name and email are required'); return;
    }
    const original = internalUsers.find(u => u.id === editingInternalUser.id);
    const { data: { user: me } } = await supabase.auth.getUser();
    if (editingInternalUser.is_active === false) {
      if (editingInternalUser.id === me.id) {
        setError('You cannot disable your own account.');
        setTimeout(() => setError(null), 4000);
        return;
      }
      if (original?.role === 'admin') {
        setError('Admin accounts cannot be disabled. Demote the user first.');
        setTimeout(() => setError(null), 4000);
        return;
      }
    }
    const emailChanged = original && (original.email || '').toLowerCase() !== (editingInternalUser.email || '').toLowerCase();
    if (emailChanged && !window.confirm(`Change ${original.full_name}'s sign-in email from ${original.email} to ${editingInternalUser.email}? They'll need to use the new address to log in.`)) {
      return;
    }
    setSaving(true);
    setError(null);
    // Email is the sign-in identifier — keep auth.users in sync with internal_users
    // before writing to the table so that a Supabase Auth rejection (e.g. duplicate
    // email) doesn't leave the table showing an address the user can't log in with.
    if (emailChanged) {
      const upd = await adminAuth.updateUser(editingInternalUser.id, { email: editingInternalUser.email });
      if (!upd.ok) { setError(`Couldn't update sign-in email: ${upd.error}`); setSaving(false); return; }
    }
    const { error: dbError } = await supabase.from('internal_users').update({
      full_name: editingInternalUser.full_name,
      email: editingInternalUser.email,
      role: editingInternalUser.role,
      job_title: editingInternalUser.job_title || null,
      department: editingInternalUser.department || null,
      is_active: editingInternalUser.is_active,
      compliance_alert_emails: Boolean(editingInternalUser.compliance_alert_emails),
      safety_incident_emails: Boolean(editingInternalUser.safety_incident_emails),
      daily_roundup_emails: Boolean(editingInternalUser.daily_roundup_emails),
    }).eq('id', editingInternalUser.id);
    if (dbError) { setError(dbError.message); setSaving(false); return; }
    setSuccess(emailChanged ? 'User updated. Sign-in email changed — they must now use the new address.' : 'Internal user updated successfully!');
    setTimeout(() => setSuccess(null), emailChanged ? 6000 : 3000);
    setEditingInternalUser(null);
    await refreshUsers();
    setSaving(false);
  };

  const findAuthUserByEmail = async (email) => {
    const { ok, user } = await adminAuth.findByEmail(email);
    return ok ? (user || null) : null;
  };

  const generateRandomPassword = (length = 16) => {
    const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*';
    const bytes = new Uint32Array(length);
    window.crypto.getRandomValues(bytes);
    let out = '';
    for (let i = 0; i < length; i++) out += charset[bytes[i] % charset.length];
    return out;
  };

  const deleteInternalUser = async (user) => {
    const { data: { user: me } } = await supabase.auth.getUser();
    if (user.id === me.id) {
      setError('You cannot delete your own account.');
      setTimeout(() => setError(null), 4000);
      return;
    }
    if (user.role === 'admin') {
      setError('Admin accounts cannot be deleted. Demote the user first if you need to remove them.');
      setTimeout(() => setError(null), 4000);
      return;
    }
    if (!window.confirm(`Delete ${user.full_name}? This permanently removes their Supply Pilot account and cannot be undone. Consider deactivating them instead if you might need to restore access later.`)) return;
    setSaving(true);
    setError(null);
    // Authenticated admin session — internal_users carries the permissive
    // baseline policy + DELETE grant, so the row delete runs without the
    // service key. The auth.users deletion goes via the admin endpoint.
    const { error: dbError } = await supabase.from('internal_users').delete().eq('id', user.id);
    if (dbError) {
      const fkLike = dbError.code === '23503' || (dbError.message || '').toLowerCase().includes('foreign key');
      setError(fkLike
        ? `${user.full_name} can't be deleted because they're still referenced by other records (e.g. as a supplier requestor). Deactivate them instead, or reassign those records first.`
        : dbError.message);
      setTimeout(() => setError(null), 6000);
      setSaving(false);
      return;
    }
    const authDel = await adminAuth.deleteUser(user.id);
    if (!authDel.ok) {
      setError(`User row removed, but couldn't delete the Supabase Auth account: ${authDel.error}. Clean it up manually in Supabase → Authentication → Users to avoid an orphaned login.`);
      setTimeout(() => setError(null), 8000);
    } else {
      setSuccess(`${user.full_name} deleted.`);
      setTimeout(() => setSuccess(null), 3000);
    }
    setEditingInternalUser(null);
    await refreshUsers();
    setSaving(false);
  };

  const generatePasswordForEdit = async (user) => {
    if (!user) return;
    const { data: { user: me } } = await supabase.auth.getUser();
    const isSelf = user.id === me.id;
    const confirmMsg = isSelf
      ? 'Generate a new password for your own account? Your current password will be replaced immediately and you may be signed out — log back in with the new password from the field below.'
      : `Generate a new password for ${user.full_name}? It will replace their current password immediately. Use the Password Email button to send it to them, or Copy Password to share it manually.`;
    if (!window.confirm(confirmMsg)) return;
    setSaving(true);
    setError(null);
    const newPassword = generateRandomPassword(16);
    const pwRes = await adminAuth.updateUser(user.id, { password: newPassword });
    if (!pwRes.ok) { setError(pwRes.error); setSaving(false); return; }
    setEditingPassword(newPassword);
    setEditingPasswordRevealed(true);
    setEditingPasswordCommitted(true);
    setSuccess(isSelf ? 'New password generated — copy it before you close this panel.' : 'New password generated.');
    setTimeout(() => setSuccess(null), isSelf ? 6000 : 2500);
    setSaving(false);
  };

  const saveEditingPasswordForUser = async (user) => {
    if (!user) return;
    const pw = (editingPassword || '').trim();
    if (pw.length < 8) {
      setError('Password must be at least 8 characters.');
      setTimeout(() => setError(null), 4000);
      return;
    }
    const { data: { user: me } } = await supabase.auth.getUser();
    const isSelf = user.id === me.id;
    const confirmMsg = isSelf
      ? 'Set this as your own new password? You may be signed out and need to log back in with it.'
      : `Set this password for ${user.full_name}? It will replace their current password immediately.`;
    if (!window.confirm(confirmMsg)) return;
    setSaving(true);
    setError(null);
    const pwRes = await adminAuth.updateUser(user.id, { password: pw });
    if (!pwRes.ok) { setError(pwRes.error); setSaving(false); return; }
    setEditingPasswordCommitted(true);
    setSuccess(isSelf ? 'Password saved — keep it somewhere safe.' : 'Password saved.');
    setTimeout(() => setSuccess(null), isSelf ? 6000 : 2500);
    setSaving(false);
  };

  const copyEditingPassword = async () => {
    if (!editingPassword) return;
    try {
      await navigator.clipboard.writeText(editingPassword);
      setSuccess('Password copied to clipboard');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Could not copy automatically — please reveal and copy manually');
      setTimeout(() => setError(null), 3000);
    }
  };

  const sendInternalPasswordEmailFromEdit = async (user) => {
    if (!user) return;
    if (!editingPassword) {
      setError('Generate a password first, then send the email.');
      setTimeout(() => setError(null), 4000);
      return;
    }
    if (!window.confirm(`Email this password to ${user.email}?`)) return;
    setSaving(true);
    setError(null);
    await sendInternalPasswordEmail(user.full_name, user.email, editingPassword);
    setSuccess(`Password emailed to ${user.email}.`);
    setTimeout(() => setSuccess(null), 3000);
    setSaving(false);
  };

  const sendSupplierPasswordEmailFromEdit = async (user) => {
    if (!user) return;
    if (!editingPassword) {
      setError('Generate a password first, then send the email.');
      setTimeout(() => setError(null), 4000);
      return;
    }
    if (!window.confirm(`Email this password to ${user.email}?`)) return;
    setSaving(true);
    setError(null);
    await sendSupplierPasswordEmail(user.full_name, user.email, editingPassword);
    setSuccess(`Password emailed to ${user.email}.`);
    setTimeout(() => setSuccess(null), 3000);
    setSaving(false);
  };

  const generatePasswordForNewInternalUser = () => {
    setNewInternalUser(prev => ({ ...prev, password: generateRandomPassword(16) }));
  };

  const generatePasswordForNewSupplierUser = () => {
    setNewSupplierUser(prev => ({ ...prev, password: generateRandomPassword(16) }));
  };


  const updateSupplierUser = async () => {
    if (!editingSupplierUser.full_name || !editingSupplierUser.email) {
      setError('Full name and email are required'); return;
    }
    const original = supplierUsers.find(u => u.id === editingSupplierUser.id);
    const emailChanged = original && (original.email || '').toLowerCase() !== (editingSupplierUser.email || '').toLowerCase();
    if (emailChanged && !window.confirm(`Change ${original.full_name}'s sign-in email from ${original.email} to ${editingSupplierUser.email}? They'll need to use the new address to log in.`)) {
      return;
    }
    setSaving(true);
    setError(null);
    if (emailChanged) {
      const upd = await adminAuth.updateUser(editingSupplierUser.id, { email: editingSupplierUser.email });
      if (!upd.ok) { setError(`Couldn't update sign-in email: ${upd.error}`); setSaving(false); return; }
    }
    const { error: dbError } = await supabase.from('supplier_users').update({
      full_name: editingSupplierUser.full_name,
      email: editingSupplierUser.email,
      supplier_id: editingSupplierUser.supplier_id,
      is_active: editingSupplierUser.is_active,
    }).eq('id', editingSupplierUser.id);
    if (dbError) { setError(dbError.message); setSaving(false); return; }
    setSuccess(emailChanged ? 'User updated. Sign-in email changed — they must now use the new address.' : 'Supplier user updated successfully!');
    setTimeout(() => setSuccess(null), emailChanged ? 6000 : 3000);
    setEditingSupplierUser(null);
    await refreshUsers();
    setSaving(false);
  };

const deleteSupplierUser = async (user) => {
    if (!window.confirm(`Delete ${user.full_name}? This permanently removes their portal account and cannot be undone. Consider disabling them instead if you might need to restore access later.`)) return;
    setSaving(true);
    setError(null);
    const { error: dbError } = await supabase.from('supplier_users').delete().eq('id', user.id);
    if (dbError) {
      const fkLike = dbError.code === '23503' || (dbError.message || '').toLowerCase().includes('foreign key');
      setError(fkLike
        ? `${user.full_name} can't be deleted because they're still referenced by other records. Disable them instead.`
        : dbError.message);
      setTimeout(() => setError(null), 6000);
      setSaving(false);
      return;
    }
    const authDel = await adminAuth.deleteUser(user.id);
    if (!authDel.ok) {
      setError(`User row removed, but couldn't delete the Supabase Auth account: ${authDel.error}. Clean it up manually in Supabase → Authentication → Users to avoid an orphaned login.`);
      setTimeout(() => setError(null), 8000);
    } else {
      setSuccess(`${user.full_name} deleted.`);
      setTimeout(() => setSuccess(null), 3000);
    }
    setEditingSupplierUser(null);
    await refreshUsers();
    setSaving(false);
  };

  const addListValue = async () => {
    if (!newValue.trim()) return;
    setSaving(true);
    const maxOrder = listValues.length > 0 ? Math.max(...listValues.map(v => v.display_order)) + 1 : 1;
    // risk_config_enabled is only meaningful for the document_type
    // list — we send the checkbox value for that list and false
    // (default) otherwise so we don't expose stray flags on other lists.
    const row = {
      list_name: selectedList,
      value: newValue.trim(),
      display_order: maxOrder,
      risk_config_enabled: selectedList === 'document_type' ? newValueRiskConfig : false,
    };
    const { error } = await supabase.from('list_values').insert([row]);
    if (!error) {
      setNewValue('');
      setNewValueRiskConfig(false);
      getListValues();
      setSuccess('Value added successfully');
      setTimeout(() => setSuccess(null), 3000);
    }
    else setError(error.message);
    setSaving(false);
  };

  const deleteListValue = async (id, label = '') => {
    if (!window.confirm(`Remove ${label ? `"${label}"` : 'this value'} from the list? This can't be undone.`)) return;
    const { error } = await supabase.from('list_values').delete().eq('id', id);
    if (error) {
      setError(`Could not remove the value: ${error.message}`);
      setTimeout(() => setError(null), 4000);
      return;
    }
    getListValues();
    setSuccess(`${label ? `"${label}"` : 'Value'} removed.`);
    setTimeout(() => setSuccess(null), 3000);
  };

  // Toggle the risk_config_enabled flag on an existing document_type
  // list value. Optimistic UI — the listValues fetch repopulates after
  // the write so the checkbox state and the Risk Flag matrix (which
  // reads its own data on tab mount) both pick up the change.
  const toggleListValueRiskConfig = async (id, nextValue) => {
    const { error } = await supabase
      .from('list_values')
      .update({ risk_config_enabled: nextValue })
      .eq('id', id);
    if (error) {
      setError(`Could not update flag: ${error.message}`);
      setTimeout(() => setError(null), 4000);
      return;
    }
    getListValues();
  };

  const roleColour = (role) => {
    switch(role) {
      case 'admin': return 'text-fusion-forest bg-[#92D400]/10';
      case 'super_user': return 'text-fusion-mid-grey bg-[#DADCCD]/10';
      case 'finance': return 'text-[#2E8FA1] bg-[#2E8FA1]/10';
      default: return 'text-[#51534A] bg-[#51534A]/10';
    }
  };

  const roleLabel = (role) => (role || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const internalQuery = internalSearch.trim().toLowerCase();
  const internalDepartments = Array.from(
    new Set(internalUsers.map(u => (u.department || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
  const internalJobTitles = Array.from(
    new Set(internalUsers.map(u => (u.job_title || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
  // Status filter values are 'enabled' / 'disabled' — multi-select means
  // both selected = match either; only one selected = match that one.
  const matchesStatus = (filter, isActive) => {
    if (filter.length === 0) return true;
    const enabled = isActive !== false;
    return (filter.includes('enabled') && enabled) || (filter.includes('disabled') && !enabled);
  };
  const filteredInternalUsers = internalUsers.filter(u => {
    if (internalJobTitleFilter.length > 0 && !internalJobTitleFilter.includes((u.job_title || '').trim())) return false;
    if (internalDepartmentFilter.length > 0 && !internalDepartmentFilter.includes((u.department || '').trim())) return false;
    if (internalRoleFilter.length > 0 && !internalRoleFilter.includes(u.role)) return false;
    if (!matchesStatus(internalStatusFilter, u.is_active)) return false;
    if (internalQuery) {
      const hit = [u.full_name, u.email, u.job_title, u.department, roleLabel(u.role)]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(internalQuery));
      if (!hit) return false;
    }
    return true;
  });
  const internalFiltersActive = internalQuery || internalJobTitleFilter.length > 0 || internalDepartmentFilter.length > 0 || internalRoleFilter.length > 0 || internalStatusFilter.length > 0;

  const supplierQuery = supplierSearch.trim().toLowerCase();
  const filteredSupplierUsers = supplierUsers.filter(u => {
    if (supplierIdFilter.length > 0 && !supplierIdFilter.includes(u.supplier_id)) return false;
    if (!matchesStatus(supplierStatusFilter, u.is_active)) return false;
    if (supplierQuery) {
      const hit = [u.full_name, u.email, u.suppliers?.supplier_name]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(supplierQuery));
      if (!hit) return false;
    }
    return true;
  });
  const supplierFiltersActive = supplierQuery || supplierIdFilter.length > 0 || supplierStatusFilter.length > 0;

  const inputClass = "w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors";
  const labelClass = "font-['Metropolis'] text-xs font-semibold text-[#51534A] block mb-1";
  const tabClass = (tab) => `font-['Metropolis'] text-sm font-semibold px-6 py-3 transition-colors inline-flex items-center gap-2 ${activeTab === tab ? 'text-fusion-forest border-b-2 border-[#92D400]' : 'text-[#51534A] hover:text-fusion-mid-grey'}`;

  if (currentUserLoading || !currentUser || !isAdmin(currentUser)) return null;

  return (
    <div className="h-screen bg-canvas flex overflow-hidden">

      {/* Sidebar — sticky to viewport so it stays visible while the main
          content scrolls. */}
      <Sidebar currentInternalUser={currentUser} />

      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Page header — inline H1 with tagline and divider beneath.
            Matches the Dashboard pattern; the divider is inset (mx-8)
            so it doesn't run edge-to-edge across the page. */}
        <div className="pt-8 flex-shrink-0">
          <div className="px-8 mb-6">
            <h1 className="fusion-h1 text-fusion-forest">Admin Panel</h1>
            <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-2">User accounts and list values. Fusion documents, risk-flag configuration and the compliance overview now live under Compliance.</p>
          </div>
          <div className="mx-8 h-px bg-[#E6E9DA]" aria-hidden="true" />
        </div>

        <div className="mx-8 border-b border-line flex flex-shrink-0">
          <button className={tabClass('users')} onClick={() => setActiveTab('users')}><TabIconUsers /> Internal Users</button>
          <button className={tabClass('suppliers')} onClick={() => setActiveTab('suppliers')}><TabIconBriefcase /> Supplier Users</button>
          <button className={tabClass('lists')} onClick={() => setActiveTab('lists')}><TabIconList /> List Values</button>
          <button className={tabClass('usermgmt')} onClick={() => setActiveTab('usermgmt')}><TabIconSettings /> User Management</button>
          <button className={tabClass('emails')} onClick={() => setActiveTab('emails')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            Emails
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-8">

          {success && <div className="bg-[#92D400]/10 border border-[#92D400]/30 rounded-xl px-4 py-3 mb-6"><p className="font-['Metropolis'] text-sm text-fusion-forest">{success}</p></div>}
          {error && <div className="bg-[#E0290A]/10 border border-[#E0290A]/30 rounded-xl px-4 py-3 mb-6"><p className="font-['Metropolis'] text-sm text-[#E0290A]">{error}</p></div>}

          {activeTab === 'users' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Metropolis'] text-lg font-bold tracking-[2px] uppercase text-ink">Internal Users</h3>
                <div className="flex items-center gap-2">
                  {/* Send today's round-up to yourself, to see the thing rather
                      than wait until tomorrow morning.
                      Sits HERE and not inside a user's notification settings on
                      purpose: it goes to whoever is signed in, never to the
                      user being edited and never to everyone opted in. Put next
                      to a person's checkbox it would read as "mail them", which
                      is not what it does. */}
                  <button
                    type="button"
                    onClick={sendDigestNow}
                    disabled={sendingDigest}
                    title="Builds today's round-up and emails it to your own address. Nobody else is contacted."
                    className="bg-white border border-line rounded-r-btn font-['Metropolis'] text-sm font-semibold px-4 py-2 text-[#51534A] hover:text-ink hover:border-[#DADCCD] disabled:opacity-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
                    {sendingDigest ? 'Sending…' : "Email me today's round-up"}
                  </button>
                  <button onClick={() => setShowAddUser(!showAddUser)} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors">
                    + Add User
                  </button>
                </div>
              </div>

              {digestResult && (
                <div className={`mb-6 rounded-xl px-4 py-2.5 border font-['Metropolis'] text-xs font-semibold ${
                  digestResult.tone === 'red'
                    ? 'bg-[#E0290A]/10 border-[#E0290A]/30 text-[#E0290A]'
                    : 'bg-[#92D400]/10 border-[#92D400]/30 text-fusion-forest'
                }`}>
                  {digestResult.msg}
                </div>
              )}

              {showAddUser && (
                <div className="bg-white border border-line rounded-xl p-6 mb-6">
                  <h4 className="font-['Metropolis'] text-sm font-semibold text-ink mb-4">Create New Internal User</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Full Name</label>
                      <input className={inputClass} value={newInternalUser.full_name} onChange={e => setNewInternalUser(prev => ({...prev, full_name: e.target.value}))} />
                    </div>
                    <div>
                      <label className={labelClass}>Email Address</label>
                      <input type="email" className={inputClass} value={newInternalUser.email} onChange={e => setNewInternalUser(prev => ({...prev, email: e.target.value}))} />
                    </div>
                    <div>
                      <label className={labelClass}>Department</label>
                      <input className={inputClass} value={newInternalUser.department} onChange={e => setNewInternalUser(prev => ({...prev, department: e.target.value}))} />
                    </div>
                    <div>
                      <label className={labelClass}>Job Title</label>
                      <input className={inputClass} value={newInternalUser.job_title} onChange={e => setNewInternalUser(prev => ({...prev, job_title: e.target.value}))} />
                    </div>
                    <div>
                      <label className={labelClass}>Account Type</label>
                      <select className={inputClass} value={newInternalUser.role} onChange={e => setNewInternalUser(prev => ({...prev, role: e.target.value}))}>
                        <option value="standard_user">Standard User</option>
                        <option value="finance">Finance</option>
                        <option value="super_user">Super User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Temporary Password</label>
                      <div className="flex gap-2">
                        <input type="text" className={inputClass + ' flex-1'} value={newInternalUser.password} onChange={e => setNewInternalUser(prev => ({...prev, password: e.target.value}))} />
                        <button type="button" onClick={generatePasswordForNewInternalUser}
                          className="flex items-center gap-2 bg-white border border-[#92D400]/40 rounded-xl text-fusion-forest font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:bg-active-green/10 transition-colors whitespace-nowrap"
                          title="Generate a secure random password">
                          <RefreshIcon /> Generate
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <span className="font-['Metropolis'] text-sm font-semibold text-fusion-mid-grey">Compliance &amp; safety alerts</span>
                    <label className="flex items-start gap-3 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(newInternalUser.compliance_alert_emails)}
                        onChange={e => setNewInternalUser(prev => ({ ...prev, compliance_alert_emails: e.target.checked }))}
                        className="mt-0.5 accent-active-green" />
                      <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                        Amber or red supplier added to a brief
                        <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">Routine — one email per amber/red supplier added.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(newInternalUser.safety_incident_emails)}
                        onChange={e => setNewInternalUser(prev => ({ ...prev, safety_incident_emails: e.target.checked }))}
                        className="mt-0.5 accent-active-green" />
                      <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                        Safety incident raised
                        <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">Rare, important — a reportable concern from post-event feedback.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(newInternalUser.daily_roundup_emails)}
                        onChange={e => setNewInternalUser(prev => ({ ...prev, daily_roundup_emails: e.target.checked }))}
                        className="mt-0.5 accent-active-green" />
                      <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                        Daily round-up
                        <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">One email each morning summarising what needs attention.</span>
                      </span>
                    </label>
                  </div>
                  <label className="flex items-start gap-3 mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(newInternalUser.send_emails)}
                      onChange={e => setNewInternalUser(prev => ({ ...prev, send_emails: e.target.checked }))}
                      className="mt-0.5 accent-active-green" />
                    <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                      Send welcome email &amp; password to the user
                      <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">
                        Default. Untick to create the account silently — you can resend either email later from this user's edit screen.
                      </span>
                    </span>
                  </label>
                  <div className="flex gap-3 mt-4">
                    <button onClick={() => setShowAddUser(false)} className="bg-white rounded-r-btn border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors">Cancel</button>
                    <button onClick={createInternalUser} disabled={saving} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                      {saving ? 'Creating...' : 'Create User'}
                    </button>
                  </div>
                </div>
              )}

              {editingInternalUser && internalUserMode === 'view' && (
                <div className="bg-white border border-[#92D400]/30 rounded-xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-['Metropolis'] text-sm font-semibold text-ink">Internal User Details</h4>
                    <span className={`font-['Metropolis'] text-xs font-medium rounded-md px-2 py-1 ${editingInternalUser.is_active === false ? 'text-[#E0290A] bg-[#E0290A]/10' : 'text-fusion-forest bg-[#92D400]/10'}`}>
                      {editingInternalUser.is_active === false ? 'Disabled' : 'Enabled'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className={labelClass}>Full Name</p>
                      <p className="font-['Metropolis'] text-sm text-ink">{editingInternalUser.full_name || '—'}</p>
                    </div>
                    <div>
                      <p className={labelClass}>Email</p>
                      <p className="font-['Metropolis'] text-sm text-fusion-mid-grey break-all">{editingInternalUser.email || '—'}</p>
                    </div>
                    <div>
                      <p className={labelClass}>Department</p>
                      <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{editingInternalUser.department || '—'}</p>
                    </div>
                    <div>
                      <p className={labelClass}>Job Title</p>
                      <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{editingInternalUser.job_title || '—'}</p>
                    </div>
                    <div>
                      <p className={labelClass}>Account Type</p>
                      <span className={`font-['Metropolis'] text-xs font-medium rounded-md px-2 py-1 ${roleColour(editingInternalUser.role)}`}>
                        {roleLabel(editingInternalUser.role)}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-line">
                    <button onClick={() => setEditingInternalUser(null)} className="bg-white rounded-r-btn border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors">Close</button>
                    <button onClick={() => { setEditingPassword(''); setEditingPasswordRevealed(false); setEditingPasswordCommitted(false); setInternalUserMode('edit'); }} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors">Edit</button>
                  </div>
                </div>
              )}

              {editingInternalUser && internalUserMode === 'edit' && (
                <div className="bg-white border border-[#92D400]/30 rounded-xl p-6 mb-6">
                  <h4 className="font-['Metropolis'] text-sm font-semibold text-ink mb-4">Edit Internal User</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Full Name *</label>
                      <input className={inputClass} value={editingInternalUser.full_name || ''} onChange={e => setEditingInternalUser(prev => ({...prev, full_name: e.target.value}))} />
                    </div>
                    <div>
                      <label className={labelClass}>Email *</label>
                      <input type="email" className={inputClass} value={editingInternalUser.email || ''} onChange={e => setEditingInternalUser(prev => ({...prev, email: e.target.value}))} />
                    </div>
                    <div>
                      <label className={labelClass}>Department</label>
                      <input className={inputClass} value={editingInternalUser.department || ''} onChange={e => setEditingInternalUser(prev => ({...prev, department: e.target.value}))} />
                    </div>
                    <div>
                      <label className={labelClass}>Job Title</label>
                      <input className={inputClass} value={editingInternalUser.job_title || ''} onChange={e => setEditingInternalUser(prev => ({...prev, job_title: e.target.value}))} />
                    </div>
                    <div>
                      <label className={labelClass}>Account Type</label>
                      <select className={inputClass} value={editingInternalUser.role || 'standard_user'} onChange={e => setEditingInternalUser(prev => ({...prev, role: e.target.value}))}>
                        <option value="standard_user">Standard User</option>
                        <option value="finance">Finance</option>
                        <option value="super_user">Super User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Status</label>
                      <select className={inputClass}
                        value={editingInternalUser.is_active === false ? 'disabled' : 'enabled'}
                        onChange={e => setEditingInternalUser(prev => ({...prev, is_active: e.target.value === 'enabled'}))}>
                        <option value="enabled">Enabled</option>
                        <option value="disabled">Disabled</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-4">
                    <span className="font-['Metropolis'] text-sm font-semibold text-fusion-mid-grey">Compliance &amp; safety alerts</span>
                    <label className="flex items-start gap-3 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(editingInternalUser.compliance_alert_emails)}
                        onChange={e => setEditingInternalUser(prev => ({ ...prev, compliance_alert_emails: e.target.checked }))}
                        className="mt-0.5 accent-active-green" />
                      <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                        Amber or red supplier added to a brief
                        <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">Routine — one email per amber/red supplier added.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(editingInternalUser.safety_incident_emails)}
                        onChange={e => setEditingInternalUser(prev => ({ ...prev, safety_incident_emails: e.target.checked }))}
                        className="mt-0.5 accent-active-green" />
                      <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                        Safety incident raised
                        <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">Rare, important — a reportable concern from post-event feedback.</span>
                      </span>
                    </label>
                    {/* Opt-in like the two above, and off by default: a daily
                        email that starts arriving unasked is how people come to
                        mute a sender — which would take the safety alerts with
                        it. */}
                    <label className="flex items-start gap-3 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(editingInternalUser.daily_roundup_emails)}
                        onChange={e => setEditingInternalUser(prev => ({ ...prev, daily_roundup_emails: e.target.checked }))}
                        className="mt-0.5 accent-active-green" />
                      <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                        Daily round-up
                        <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">One email each morning summarising what needs attention.</span>
                      </span>
                    </label>
                  </div>

                  <div className="mt-4">
                    <label className={labelClass}>Password</label>
                    <div className="flex gap-2 items-stretch">
                      <div className="relative flex-1">
                        <input
                          type={editingPasswordRevealed ? 'text' : 'password'}
                          value={editingPassword}
                          onChange={e => { setEditingPassword(e.target.value); setEditingPasswordCommitted(false); }}
                          placeholder="Type a new password or click Generate"
                          className="w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 pr-10 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400]" />
                        <button type="button"
                          onClick={() => setEditingPasswordRevealed(prev => !prev)}
                          disabled={!editingPassword}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#51534A] hover:text-active-green transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={editingPasswordRevealed ? 'Hide password' : 'Show password'}>
                          <EyeIcon show={editingPasswordRevealed} />
                        </button>
                      </div>
                      <button onClick={() => saveEditingPasswordForUser(editingInternalUser)} disabled={saving || !editingPassword || editingPassword.length < 8}
                        title={editingPassword && editingPassword.length < 8 ? 'Password must be at least 8 characters' : 'Save the typed password'}
                        className="flex items-center gap-2 bg-[#92D400] border border-[#92D400] rounded-xl text-[#131313] font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:bg-[#7eb800] transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                        <KeyIcon /> Save Password
                      </button>
                      <button onClick={() => generatePasswordForEdit(editingInternalUser)} disabled={saving}
                        className="flex items-center gap-2 bg-white border border-[#92D400]/40 rounded-xl text-fusion-forest font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:bg-active-green/10 transition-colors disabled:opacity-50 whitespace-nowrap">
                        <RefreshIcon /> Generate Password
                      </button>
                      <button onClick={copyEditingPassword} disabled={!editingPassword}
                        className="flex items-center gap-2 bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:border-active-green hover:text-active-green transition-colors disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap">
                        <CopyIcon /> Copy Password
                      </button>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-line flex flex-wrap items-center gap-3">
                    <p className="font-['Metropolis'] text-xs font-semibold text-[#51534A] mr-2">Send Email:</p>
                    <button onClick={() => resendInternalWelcomeEmail(editingInternalUser)} disabled={saving}
                      className="flex items-center gap-2 bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:border-active-green hover:text-active-green transition-colors disabled:opacity-50">
                      <EnvelopeIcon /> Welcome Email
                    </button>
                    <button onClick={() => sendInternalPasswordEmailFromEdit(editingInternalUser)} disabled={saving || !editingPassword || !editingPasswordCommitted}
                      title={!editingPassword ? 'Generate or type a password first' : !editingPasswordCommitted ? 'Save the typed password before emailing it' : 'Email this password to the user'}
                      className="flex items-center gap-2 bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:border-[#2E8FA1] hover:text-[#2E8FA1] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                      <KeyIcon /> Password Email
                    </button>
                  </div>

                  <div className="flex justify-between gap-3 mt-6 pt-4 border-t border-line">
                    <button onClick={() => deleteInternalUser(editingInternalUser)} disabled={saving}
                      className="flex items-center gap-2 bg-white border border-[#E0290A]/40 rounded-xl text-[#E0290A] font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:bg-[#E0290A]/10 transition-colors disabled:opacity-50">
                      <TrashIcon /> Delete User
                    </button>
                    <div className="flex gap-3">
                      <button onClick={() => setEditingInternalUser(null)} className="bg-white rounded-r-btn border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors">Cancel</button>
                      <button onClick={updateInternalUser} disabled={saving} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Search bar — column-level filters live inline in the
                  table header below (Job Title, Department, Account Type,
                  Status) so each column owns its own filter trigger
                  rather than the row of pills they used to sit in. */}
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <SearchInput
                  value={internalSearch}
                  onChange={setInternalSearch}
                  placeholder="Search by name, email, job title, department or account type..."
                  className="flex-1 min-w-[280px]" />
                {internalFiltersActive && (
                  <button
                    type="button"
                    onClick={() => { setInternalSearch(''); setInternalJobTitleFilter([]); setInternalDepartmentFilter([]); setInternalRoleFilter([]); setInternalStatusFilter([]); }}
                    className="font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-active-green transition-colors px-3 py-2">
                    Clear all
                  </button>
                )}
              </div>

              {loading ? (
                <p className="font-['Metropolis'] text-sm text-[#51534A]">Loading...</p>
              ) : filteredInternalUsers.length === 0 ? (
                <p className="font-['Metropolis'] text-sm text-[#51534A]">{internalFiltersActive ? 'No users match your filters.' : 'No internal users yet.'}</p>
              ) : (
                <div className="overflow-x-auto">
                  {/* Internal users: 7 columns, three of them FilterDropdowns, which are controls not text and do not compress. */}
                  <table className="w-full min-w-[1080px]">
                    <thead>
                      <tr className="border-b border-line align-bottom">
                        <th className="text-left font-['Metropolis'] text-xs font-semibold text-[#51534A] pb-3 pr-4">Name</th>
                        <th className="text-left font-['Metropolis'] text-xs font-semibold text-[#51534A] pb-3 pr-4">Email</th>
                        <th className="text-left pb-3 pr-4">
                          <FilterDropdown
                            label="Job Title"
                            options={internalJobTitles.map(j => ({ value: j, label: j }))}
                            selected={internalJobTitleFilter}
                            onChange={setInternalJobTitleFilter} />
                        </th>
                        <th className="text-left pb-3 pr-4">
                          <FilterDropdown
                            label="Department"
                            options={internalDepartments.map(d => ({ value: d, label: d }))}
                            selected={internalDepartmentFilter}
                            onChange={setInternalDepartmentFilter} />
                        </th>
                        <th className="text-left pb-3 pr-4">
                          <FilterDropdown
                            label="Account Type"
                            options={[
                              { value: 'admin', label: 'Admin' },
                              { value: 'super_user', label: 'Super User' },
                              { value: 'finance', label: 'Finance' },
                              { value: 'standard_user', label: 'Standard User' },
                            ]}
                            selected={internalRoleFilter}
                            onChange={setInternalRoleFilter} />
                        </th>
                        <th className="text-left pb-3">
                          <FilterDropdown
                            label="Status"
                            options={[
                              { value: 'enabled', label: 'Enabled' },
                              { value: 'disabled', label: 'Disabled' },
                            ]}
                            selected={internalStatusFilter}
                            onChange={setInternalStatusFilter} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInternalUsers.map(user => (
                        <tr key={user.id}
                          onClick={() => { setEditingInternalUser(user); setInternalUserMode('view'); }}
                          className="border-b border-line hover:bg-white transition-colors cursor-pointer">
                          <td className="py-3 pr-4"><p className="font-['Metropolis'] text-sm text-ink">{user.full_name}</p></td>
                          <td className="py-3 pr-4"><p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{user.email}</p></td>
                          <td className="py-3 pr-4"><p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{user.job_title || '—'}</p></td>
                          <td className="py-3 pr-4"><p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{user.department || '—'}</p></td>
                          <td className="py-3 pr-4">
                            <span className={`font-['Metropolis'] text-xs font-medium rounded-md px-2 py-1 ${roleColour(user.role)}`}>
                              {roleLabel(user.role)}
                            </span>
                          </td>
                          <td className="py-3">
                            <span className={`font-['Metropolis'] text-xs font-medium rounded-md px-2 py-1 ${user.is_active ? 'text-fusion-forest bg-[#92D400]/10' : 'text-[#E0290A] bg-[#E0290A]/10'}`}>
                              {user.is_active ? 'Enabled' : 'Disabled'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'suppliers' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Metropolis'] text-lg font-bold tracking-[2px] uppercase text-ink">Supplier Users</h3>
                <button onClick={() => setShowAddSupplierUser(!showAddSupplierUser)} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors">
                  + Add Supplier User
                </button>
              </div>

              {showAddSupplierUser && (
                <div className="bg-white border border-line rounded-xl p-6 mb-6">
                  <h4 className="font-['Metropolis'] text-sm font-semibold text-ink mb-4">Create New Supplier User</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Full Name</label>
                      <input className={inputClass} value={newSupplierUser.full_name} onChange={e => setNewSupplierUser(prev => ({...prev, full_name: e.target.value}))} />
                    </div>
                    <div>
                      <label className={labelClass}>Email Address</label>
                      <input type="email" className={inputClass} value={newSupplierUser.email} onChange={e => setNewSupplierUser(prev => ({...prev, email: e.target.value}))} />
                    </div>
                    <div>
                      <label className={labelClass}>Link to Supplier</label>
                      <SupplierCombobox
                        value={newSupplierUser.supplier_id}
                        onChange={(id) => setNewSupplierUser(prev => ({...prev, supplier_id: id}))}
                        suppliers={suppliers}
                        inputClass={inputClass}
                        placeholder="Type to search suppliers..." />
                    </div>
                    <div>
                      <label className={labelClass}>Temporary Password</label>
                      <div className="flex gap-2">
                        <input type="text" className={inputClass + ' flex-1'} value={newSupplierUser.password} onChange={e => setNewSupplierUser(prev => ({...prev, password: e.target.value}))} />
                        <button type="button" onClick={generatePasswordForNewSupplierUser}
                          className="flex items-center gap-2 bg-white border border-[#92D400]/40 rounded-xl text-fusion-forest font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:bg-active-green/10 transition-colors whitespace-nowrap"
                          title="Generate a secure random password">
                          <RefreshIcon /> Generate
                        </button>
                      </div>
                    </div>
                  </div>
                  <label className="flex items-start gap-3 mt-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(newSupplierUser.send_emails)}
                      onChange={e => setNewSupplierUser(prev => ({ ...prev, send_emails: e.target.checked }))}
                      className="mt-0.5 accent-active-green" />
                    <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                      Send welcome email &amp; password to the user
                      <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">
                        Default. Untick to create the account silently — you can resend either email later from this user's edit screen.
                      </span>
                    </span>
                  </label>
                  <div className="flex gap-3 mt-4">
                    <button onClick={() => setShowAddSupplierUser(false)} className="bg-white rounded-r-btn border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors">Cancel</button>
                    <button onClick={createSupplierUser} disabled={saving} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                      {saving ? 'Creating...' : 'Create Supplier User'}
                    </button>
                  </div>
                </div>
              )}

              {editingSupplierUser && supplierUserMode === 'view' && (
                <div className="bg-white border border-[#92D400]/30 rounded-xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-['Metropolis'] text-sm font-semibold text-ink">Supplier User Details</h4>
                    <span className={`font-['Metropolis'] text-xs font-medium rounded-md px-2 py-1 ${editingSupplierUser.is_active === false ? 'text-[#E0290A] bg-[#E0290A]/10' : 'text-fusion-forest bg-[#92D400]/10'}`}>
                      {editingSupplierUser.is_active === false ? 'Disabled' : 'Enabled'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className={labelClass}>Full Name</p>
                      <p className="font-['Metropolis'] text-sm text-ink">{editingSupplierUser.full_name || '—'}</p>
                    </div>
                    <div>
                      <p className={labelClass}>Email</p>
                      <p className="font-['Metropolis'] text-sm text-fusion-mid-grey break-all">{editingSupplierUser.email || '—'}</p>
                    </div>
                    <div>
                      <p className={labelClass}>Linked Supplier</p>
                      {(() => {
                        const linkedName = editingSupplierUser.suppliers?.supplier_name || suppliers.find(s => s.id === editingSupplierUser.supplier_id)?.supplier_name;
                        if (editingSupplierUser.supplier_id && linkedName) {
                          return (
                            <button
                              type="button"
                              onClick={() => navigate(`/suppliers/${editingSupplierUser.supplier_id}`)}
                              className="font-['Metropolis'] text-sm text-fusion-forest hover:underline">
                              {linkedName}
                            </button>
                          );
                        }
                        return <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">—</p>;
                      })()}
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-line">
                    <button onClick={() => setEditingSupplierUser(null)} className="bg-white rounded-r-btn border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors">Close</button>
                    <button onClick={() => { setEditingPassword(''); setEditingPasswordRevealed(false); setEditingPasswordCommitted(false); setSupplierUserMode('edit'); }} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors">Edit</button>
                  </div>
                </div>
              )}

              {editingSupplierUser && supplierUserMode === 'edit' && (
                <div className="bg-white border border-[#92D400]/30 rounded-xl p-6 mb-6">
                  <h4 className="font-['Metropolis'] text-sm font-semibold text-ink mb-4">Edit Supplier User</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Full Name *</label>
                      <input className={inputClass} value={editingSupplierUser.full_name || ''} onChange={e => setEditingSupplierUser(prev => ({...prev, full_name: e.target.value}))} />
                    </div>
                    <div>
                      <label className={labelClass}>Email *</label>
                      <input type="email" className={inputClass} value={editingSupplierUser.email || ''} onChange={e => setEditingSupplierUser(prev => ({...prev, email: e.target.value}))} />
                    </div>
                    <div>
                      <label className={labelClass}>Link to Supplier</label>
                      <SupplierCombobox
                        value={editingSupplierUser.supplier_id || ''}
                        onChange={(id) => setEditingSupplierUser(prev => ({...prev, supplier_id: id}))}
                        suppliers={suppliers}
                        inputClass={inputClass}
                        placeholder="Type to search suppliers..." />
                    </div>
                    <div>
                      <label className={labelClass}>Status</label>
                      <select className={inputClass}
                        value={editingSupplierUser.is_active === false ? 'disabled' : 'enabled'}
                        onChange={e => setEditingSupplierUser(prev => ({...prev, is_active: e.target.value === 'enabled'}))}>
                        <option value="enabled">Enabled</option>
                        <option value="disabled">Disabled</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className={labelClass}>Password</label>
                    <div className="flex gap-2 items-stretch">
                      <div className="relative flex-1">
                        <input
                          type={editingPasswordRevealed ? 'text' : 'password'}
                          value={editingPassword}
                          onChange={e => { setEditingPassword(e.target.value); setEditingPasswordCommitted(false); }}
                          placeholder="Type a new password or click Generate"
                          className="w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 pr-10 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400]" />
                        <button type="button"
                          onClick={() => setEditingPasswordRevealed(prev => !prev)}
                          disabled={!editingPassword}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#51534A] hover:text-active-green transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={editingPasswordRevealed ? 'Hide password' : 'Show password'}>
                          <EyeIcon show={editingPasswordRevealed} />
                        </button>
                      </div>
                      <button onClick={() => saveEditingPasswordForUser(editingSupplierUser)} disabled={saving || !editingPassword || editingPassword.length < 8}
                        title={editingPassword && editingPassword.length < 8 ? 'Password must be at least 8 characters' : 'Save the typed password'}
                        className="flex items-center gap-2 bg-[#92D400] border border-[#92D400] rounded-xl text-[#131313] font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:bg-[#7eb800] transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                        <KeyIcon /> Save Password
                      </button>
                      <button onClick={() => generatePasswordForEdit(editingSupplierUser)} disabled={saving}
                        className="flex items-center gap-2 bg-white border border-[#92D400]/40 rounded-xl text-fusion-forest font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:bg-active-green/10 transition-colors disabled:opacity-50 whitespace-nowrap">
                        <RefreshIcon /> Generate Password
                      </button>
                      <button onClick={copyEditingPassword} disabled={!editingPassword}
                        className="flex items-center gap-2 bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:border-active-green hover:text-active-green transition-colors disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap">
                        <CopyIcon /> Copy Password
                      </button>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-line flex flex-wrap items-center gap-3">
                    <p className="font-['Metropolis'] text-xs font-semibold text-[#51534A] mr-2">Send Email:</p>
                    <button onClick={() => resendSupplierWelcomeEmail(editingSupplierUser)} disabled={saving}
                      className="flex items-center gap-2 bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:border-active-green hover:text-active-green transition-colors disabled:opacity-50">
                      <EnvelopeIcon /> Welcome Email
                    </button>
                    <button onClick={() => sendSupplierPasswordEmailFromEdit(editingSupplierUser)} disabled={saving || !editingPassword || !editingPasswordCommitted}
                      title={!editingPassword ? 'Generate or type a password first' : !editingPasswordCommitted ? 'Save the typed password before emailing it' : 'Email this password to the user'}
                      className="flex items-center gap-2 bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:border-[#2E8FA1] hover:text-[#2E8FA1] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                      <KeyIcon /> Password Email
                    </button>
                  </div>

                  <div className="flex justify-between gap-3 mt-6 pt-4 border-t border-line">
                    <button onClick={() => deleteSupplierUser(editingSupplierUser)} disabled={saving}
                      className="flex items-center gap-2 bg-white border border-[#E0290A]/40 rounded-xl text-[#E0290A] font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:bg-[#E0290A]/10 transition-colors disabled:opacity-50">
                      <TrashIcon /> Delete User
                    </button>
                    <div className="flex gap-3">
                      <button onClick={() => setEditingSupplierUser(null)} className="bg-white rounded-r-btn border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors">Cancel</button>
                      <button onClick={updateSupplierUser} disabled={saving} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6 flex flex-wrap items-center gap-3">
                <SearchInput
                  value={supplierSearch}
                  onChange={setSupplierSearch}
                  placeholder="Search by name, email or supplier..."
                  className="flex-1 min-w-[280px]" />
                <FilterDropdown
                  label="Supplier"
                  options={suppliers.map(s => ({ value: s.id, label: s.supplier_name }))}
                  selected={supplierIdFilter}
                  onChange={setSupplierIdFilter} />
                <FilterDropdown
                  label="Status"
                  options={[
                    { value: 'enabled', label: 'Enabled' },
                    { value: 'disabled', label: 'Disabled' },
                  ]}
                  selected={supplierStatusFilter}
                  onChange={setSupplierStatusFilter} />
                {supplierFiltersActive && (
                  <button
                    type="button"
                    onClick={() => { setSupplierSearch(''); setSupplierIdFilter([]); setSupplierStatusFilter([]); }}
                    className="font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-active-green transition-colors px-3 py-2">
                    Clear all
                  </button>
                )}
              </div>

              {loading ? (
                <p className="font-['Metropolis'] text-sm text-[#51534A]">Loading...</p>
              ) : filteredSupplierUsers.length === 0 ? (
                <p className="font-['Metropolis'] text-sm text-[#51534A]">{supplierFiltersActive ? 'No users match your filters.' : 'No supplier users yet.'}</p>
              ) : (
                <div className="overflow-x-auto">
                  {/* Supplier users: Name, Email, Supplier, Status + actions -- three free-text columns. */}
                  <table className="w-full min-w-[820px]">
                    <thead>
                      <tr className="border-b border-line">
                        <th className="text-left font-['Metropolis'] text-xs font-semibold text-[#51534A] pb-3 pr-4">Name</th>
                        <th className="text-left font-['Metropolis'] text-xs font-semibold text-[#51534A] pb-3 pr-4">Email</th>
                        <th className="text-left font-['Metropolis'] text-xs font-semibold text-[#51534A] pb-3 pr-4">Supplier</th>
                        <th className="text-left font-['Metropolis'] text-xs font-semibold text-[#51534A] pb-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSupplierUsers.map(user => (
                        <tr key={user.id}
                          onClick={() => { setEditingSupplierUser(user); setSupplierUserMode('view'); }}
                          className="border-b border-line hover:bg-white transition-colors cursor-pointer">
                          <td className="py-3 pr-4"><p className="font-['Metropolis'] text-sm text-ink">{user.full_name}</p></td>
                          <td className="py-3 pr-4"><p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{user.email}</p></td>
                          <td className="py-3 pr-4"><p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{user.suppliers?.supplier_name || '—'}</p></td>
                          <td className="py-3">
                            <span className={`font-['Metropolis'] text-xs font-medium rounded-md px-2 py-1 ${user.is_active ? 'text-fusion-forest bg-[#92D400]/10' : 'text-[#E0290A] bg-[#E0290A]/10'}`}>
                              {user.is_active ? 'Enabled' : 'Disabled'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'lists' && (
            <div>
              {/* Brief auto-archive — system-wide default. Applies to both
                  Alchemy and the supplier portal; per-brief override lives on
                  each brief. */}
              <div className="bg-white border border-line rounded-xl p-5 mb-8 max-w-2xl">
                <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mb-3">Briefs · Archive</p>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={archiveEnabled}
                    disabled={archiveSaving}
                    onChange={e => saveArchiveSettings(e.target.checked, archiveWeeks)}
                    className="w-5 h-5 accent-active-green mt-0.5 flex-shrink-0"
                  />
                  <span>
                    <span className="font-['Metropolis'] text-sm font-bold text-ink">Auto-archive completed briefs</span>
                    <span className="block font-['Metropolis'] text-xs text-fusion-mid-grey mt-1 leading-relaxed">
                      Once a brief is completed, it's archived automatically after the period below. Applies to both Alchemy and the supplier portal. You can override on individual briefs, archive earlier, or unarchive at any time.
                    </span>
                  </span>
                </label>
                <div className="flex items-center gap-2 mt-4 pl-8">
                  <span className="font-['Metropolis'] text-xs text-[#51534A]">Archive after</span>
                  <input
                    type="number"
                    min="1"
                    max="52"
                    value={archiveWeeks}
                    disabled={archiveSaving || !archiveEnabled}
                    onChange={e => setArchiveWeeks(e.target.value)}
                    onBlur={e => saveArchiveSettings(archiveEnabled, e.target.value)}
                    className="w-16 bg-white border border-line rounded-md px-2 py-1 text-sm text-ink outline-none focus:border-[#92D400] font-['Metropolis'] disabled:opacity-50"
                  />
                  <span className="font-['Metropolis'] text-xs text-[#51534A]">weeks after completion</span>
                </div>
              </div>

              {/* Post-event feedback — value threshold. Feedback is only
                  requested for awards at or above this amount. 0 = every
                  award. Read by BriefDetail via feedbackState(thresholdGBP). */}
              <div className="bg-white border border-line rounded-xl p-5 mb-8 max-w-2xl">
                <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mb-3">Feedback · Value threshold</p>
                <span className="font-['Metropolis'] text-sm font-bold text-ink">Only ask for feedback above a value</span>
                <span className="block font-['Metropolis'] text-xs text-fusion-mid-grey mt-1 leading-relaxed">
                  Post-event feedback is only requested for awards at or above this amount (ex-VAT). Set it to £0 to ask for feedback on every award. Changing it affects which briefs show a feedback prompt from now on.
                </span>
                <div className="flex items-center gap-2 mt-4">
                  <span className="font-['Metropolis'] text-xs text-[#51534A]">Ask for feedback at or above</span>
                  <span className="font-['Metropolis'] text-sm text-ink">£</span>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={feedbackThreshold}
                    disabled={feedbackThresholdSaving}
                    onChange={e => setFeedbackThreshold(e.target.value)}
                    onBlur={e => saveFeedbackThreshold(e.target.value)}
                    className="w-28 bg-white border border-line rounded-md px-2 py-1 text-sm text-ink outline-none focus:border-[#92D400] font-['Metropolis'] disabled:opacity-50"
                  />
                  <span className="font-['Metropolis'] text-xs text-[#51534A]">ex-VAT</span>
                </div>
              </div>

              <h3 className="font-['Metropolis'] text-lg font-bold tracking-[2px] uppercase text-ink mb-6">Manage List Values</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                <div className="col-span-1">
                  <p className={labelClass}>Select List</p>
                  <div className="space-y-1">
                    {listNames.map(list => (
                      <button key={list.key} onClick={() => setSelectedList(list.key)}
                        className={`w-full text-left px-3 py-2 font-['Metropolis'] text-sm font-semibold transition-colors ${selectedList === list.key ? 'text-fusion-forest bg-[#92D400]/10' : 'text-fusion-mid-grey hover:bg-white'}`}>
                        {list.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-span-3">
                  {selectedList === 'document_type' && (
                    <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4">
                      These appear in the Document Type dropdown when uploading a Fusion Document, in addition to the built-in types (Terms &amp; Conditions, Compliance, Privacy Policy, Code of Conduct, Supplier Handbook, Supplier Enhancement, Other). The built-in types can't be edited or removed because parts of the app depend on them.
                    </p>
                  )}
                  <div className="flex gap-3 mb-3">
                    <input className={inputClass} value={newValue} onChange={e => setNewValue(e.target.value)}
                      placeholder="Add new value..." onKeyDown={e => e.key === 'Enter' && addListValue()} />
                    <button onClick={addListValue} disabled={saving}
                      className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-sm font-semibold px-6 py-2 hover:bg-[#6a9a00] transition-colors whitespace-nowrap">
                      Add Value
                    </button>
                  </div>
                  {/* Risk-flag tickbox only makes sense for the
                      document_type list — when enabled, documents
                      uploaded under this type appear in the Risk Flag
                      Configuration grid alongside the built-in
                      'compliance' type. */}
                  {selectedList === 'document_type' && (
                    <label className="flex items-center gap-2 mb-6 cursor-pointer w-fit">
                      <input
                        type="checkbox"
                        checked={newValueRiskConfig}
                        onChange={e => setNewValueRiskConfig(e.target.checked)}
                        className="w-4 h-4 accent-active-green" />
                      <span className="font-['Metropolis'] text-xs text-fusion-mid-grey">Show in Risk Flag Configuration</span>
                    </label>
                  )}
                  <div className="border border-line rounded-xl overflow-hidden">
                    {listValues.length === 0 ? (
                      <p className="font-['Metropolis'] text-sm text-[#51534A] p-6">No values in this list yet.</p>
                    ) : (
                      listValues.map(value => (
                        <div key={value.id} className="flex items-center justify-between gap-4 px-4 py-3 border-b border-line last:border-0 hover:bg-white transition-colors">
                          <p className="font-['Metropolis'] text-sm text-ink">{value.value}</p>
                          <div className="flex items-center gap-4 flex-shrink-0">
                            {selectedList === 'document_type' && (
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!!value.risk_config_enabled}
                                  onChange={e => toggleListValueRiskConfig(value.id, e.target.checked)}
                                  className="w-4 h-4 accent-active-green" />
                                <span className="font-['Metropolis'] text-[10px] font-bold tracking-[1px] uppercase text-[#51534A]">In Risk Flag Config</span>
                              </label>
                            )}
                            <button onClick={() => deleteListValue(value.id, value.value)}
                              className="font-['Metropolis'] text-xs font-medium text-[#51534A] hover:text-[#E0290A] transition-colors">
                              Remove
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'usermgmt' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Metropolis'] text-lg font-bold tracking-[2px] uppercase text-ink">User Management</h3>
                <span className="fusion-field-label bg-white border border-line rounded-md px-3 py-1">Coming soon</span>
              </div>
              <div className="bg-white border border-line rounded-xl p-8">
                <p className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed mb-4">
                  This is where we'll define user roles and control what different
                  types of internal user can see and do across Supply Pilot — e.g.
                  who can create briefs, edit suppliers, view financial figures,
                  or manage admin settings.
                </p>
                <p className="font-['Metropolis'] text-sm text-[#51534A] leading-relaxed">
                  Today every internal user with the <span className="text-fusion-forest">admin</span> flag has full
                  access. The plan is to layer role-based permissions on top of that so we can grant
                  read-only, brief-only, or supplier-only access without giving away the whole panel.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'emails' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Metropolis'] text-lg font-bold tracking-[2px] uppercase text-ink">Emails</h3>
              </div>
              <EmailTemplatesPanel currentUser={currentUser} />
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
